import { useId } from 'react';
import { clsx } from 'clsx';
import { Checkbox as BaseCheckbox } from '@headlessui/react';

interface CheckboxWithLabelProps {
  /** When `true`, the checkbox is checked. */
  checked?: boolean;

  /** Callback fired when the checkbox is toggled. */
  onChange: (checked: boolean) => void;

  /** Label text linked to the checkbox for accessibility. */
  label: React.ReactNode;

  /** Optional id for the checkbox control; autogenerated when omitted. */
  id?: string;

  /** When `true`, the checkbox and label are disabled. */
  disabled?: boolean;

  /** Optional className for the label wrapper. */
  className?: string;
}

export function CheckboxWithLabel({
  label,
  id,
  disabled = false,
  className,
  checked = false,
  onChange
}: CheckboxWithLabelProps) {
  const generatedId = useId();
  const checkboxId = id ?? generatedId;
  const labelId = `${checkboxId}-label`;

  function handleLabelClick(event: React.MouseEvent<HTMLLabelElement>) {
    if (disabled) {
      return;
    }

    if ((event.target as HTMLElement).closest('button')) {
      return;
    }

    onChange(!checked);
  }

  return (
    <label
      onClick={handleLabelClick}
      className={clsx(
        'flex items-start gap-2 font-sans',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className
      )}
    >
      <BaseCheckbox
        id={checkboxId}
        checked={checked}
        disabled={disabled}
        aria-labelledby={labelId}
        onChange={disabled ? undefined : onChange}
        className="text-xl text-action flex-shrink-0"
      >
        {({ checked: isChecked, indeterminate }) =>
          indeterminate ? (
            <i className="fa-solid fa-square-minus" aria-hidden="true" />
          ) : isChecked ? (
            <i className="fa-solid fa-square-check" aria-hidden="true" />
          ) : (
            <i className="fa-regular fa-square" aria-hidden="true" />
          )
        }
      </BaseCheckbox>
      <span
        id={labelId}
        className="font-sans text-charcoal-500 text-left pt-0.5"
      >
        {label}
      </span>
    </label>
  );
}
